import { CloudActions } from '@portal/portal-core';
import type { AnyAction } from 'redux';
import { AuthorizedDevices as AuthorizedDeviceActions } from '../actions';

type Status<IdKey extends string> = {
  [K in IdKey]: string;
} & {
  loading: boolean;
  success: boolean;
  result: unknown;
};

export type AuthorizedDeviceStateType = {
  devices: Record<string, unknown>;
  removalsRemaining: Record<string, unknown>;
  removalStatus: Status<'deviceId'>;
  renameStatus: Status<'deviceId'>;
  createDeviceStatus: Status<'productAccessId'>;
  createEotStatus: Status<'authorizedDeviceId'>;
  exportEotStatus: Status<'eotId'>;
};

export type AuthoriedDeviceSagaStatusType = Omit<
  AuthorizedDeviceStateType,
  'devices' | 'removalsRemaining'
>;

export const initState: AuthorizedDeviceStateType = {
  devices: {},
  removalsRemaining: {},
  removalStatus: { deviceId: '', loading: false, success: false, result: null },
  renameStatus: { deviceId: '', loading: false, success: false, result: null },
  createDeviceStatus: { productAccessId: '', loading: false, success: false, result: null },
  createEotStatus: { authorizedDeviceId: '', loading: false, success: false, result: null },
  exportEotStatus: { eotId: '', loading: false, success: false, result: null },
};

export default function authorizedDevices(state = initState, action: AnyAction) {
  const { success, result } = action?.payload || {};
  switch (action.type) {
    case CloudActions.AUTHORIZED_DEVICES:
      return {
        ...state,
        devices: {
          ...state.devices,
          ...action.authorizedDevices,
        },
      };
    case CloudActions.AUTHORIZED_DEVICES_REMOVALS_REMAINING:
      return {
        ...state,
        removalsRemaining: {
          ...state.removalsRemaining,
          ...action.authorizedDeviceRemovalsRemaining,
        },
      };
    case AuthorizedDeviceActions.DEACTIVATE_AUTHORIZED_DEVICE:
      return {
        ...state,
        removalStatus: {
          ...initState.removalStatus,
          deviceId: action.payload.deviceId,
          loading: true,
        },
      };
    case AuthorizedDeviceActions.DEACTIVATE_AUTHORIZED_DEVICE_RESULT:
      return {
        ...state,
        removalStatus: {
          ...state.removalStatus,
          result,
          success,
          loading: false,
        },
      };
    case AuthorizedDeviceActions.RENAME_AUTHORIZED_DEVICE:
      return {
        ...state,
        renameStatus: {
          ...initState.renameStatus,
          deviceId: action.payload.deviceId,
          loading: true,
        },
      };
    case AuthorizedDeviceActions.RENAME_AUTHORIZED_DEVICE_RESULT:
      return {
        ...state,
        renameStatus: {
          ...state.renameStatus,
          result,
          success,
          loading: false,
        },
      };
    case AuthorizedDeviceActions.CREATE_AUTHORIZED_DEVICE:
      return {
        ...state,
        createDeviceStatus: {
          ...initState.createDeviceStatus,
          productAccessId: action.payload.productAccessId,
          loading: true,
        },
      };
    case AuthorizedDeviceActions.CREATE_AUTHORIZED_DEVICE_RESULT:
      return {
        ...state,
        createDeviceStatus: {
          ...state.createDeviceStatus,
          result,
          success,
          loading: false,
        },
      };
    case AuthorizedDeviceActions.CREATE_EXTENDED_OFFLINE_TOKEN:
      return {
        ...state,
        createEotStatus: {
          ...initState.createEotStatus,
          authorizedDeviceId: action.payload.authorizedDeviceId,
          loading: true,
        },
      };
    case AuthorizedDeviceActions.CREATE_EXTENDED_OFFLINE_TOKEN_RESULT:
      return {
        ...state,
        createEotStatus: {
          ...state.createEotStatus,
          result,
          success,
          loading: false,
        },
      };
    case AuthorizedDeviceActions.EXPORT_EXTENDED_OFFLINE_TOKEN_SAGA:
      return {
        ...state,
        exportEotStatus: {
          ...initState.exportEotStatus,
          eotId: action.payload.eotId,
          loading: true,
        },
      };
    case AuthorizedDeviceActions.EXPORT_EXTENDED_OFFLINE_TOKEN_SAGA_RESULT:
      return {
        ...state,
        exportEotStatus: {
          ...state.exportEotStatus,
          result,
          success,
          loading: false,
        },
      };
    case CloudActions.LOGOUT:
      return initState;
    default:
      return state;
  }
}
